/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useState } from "react";

import { useFormat, useLocale } from "@superweb/intl";
import { cssFns } from "@superweb/css";
import { useTypo, useUiColors } from "@superweb/ui";

import { useAction, useQuery } from "@fleet/api";

import {
  searchParamTypes as t,
  useMakeLink,
  useNavigate,
  useSearchParams,
} from "#internal/router-new";
import {
  AppLayout,
  LinkPanel,
  NotFound,
  Offer,
  OfferDeprecated,
  PageSpinner,
} from "#internal/ui";
import { useMessage } from "#internal/intl";
import { useLogAction } from "#internal/analytics";
import { FormHeader } from "#internal/ui/form";
import {
  useErrorMessages,
  useSubmitErrorMessages,
} from "#internal/utils/validation";
import SerbiaOfferPng from "#internal/images/serbia-offer.png";
import SerbiaOfferWebp from "#internal/images/serbia-offer.webp";
import SerbiaOfferAvif from "#internal/images/serbia-offer.avif";

import { getSteps } from "../steps";
import { useFlag } from "@superweb/flags";

const Content = ({
  refId,
  sessionId,
  specification,
  proId,
  leadId,
  isMultipleEmploymentTypes,
  employmentType,
  isIframe,
  confirmationId,
}: {
  refId: string;
  sessionId?: string;
  specification?: string;
  proId?: boolean;
  leadId?: string;
  isMultipleEmploymentTypes?: boolean;
  employmentType?: "selfemployed" | "individual_entrepreneur" | "park_employee";
  isIframe: boolean;
  confirmationId?: string;
}) => {
  const logActionMultiProfessionForm = useLogAction("multi_profession_form");
  const logActionContact = useLogAction("contact");
  const locale = useLocale();
  const message = useMessage();
  const professionsConfig = useProfessionsConfig();
  const employmentTypeNames = useEmploymentTypeNames();
  const { formatList } = useFormat();
  const navigate = useNavigate();
  const errorMessages = useErrorMessages();
  const typo = useTypo();
  const uiColors = useUiColors();
  const flag = useFlag();
  const makeLink = useMakeLink();

  const [formErrorMessage, setFormErrorMessage] = useState<string>();

  const { response } = useQuery({
    url: "/fleet-forms/selfreg/v1/park/info",
    method: "GET",
    headers: {
      language: locale.toString(),
      "x-session-id": sessionId,
    },
    query: { ref_id: refId },
  });
  const parkInfo = response.status === 200 ? response.body : undefined;

  const submitErrorMessages = useSubmitErrorMessages(
    parkInfo?.country_code ?? "",
  );

  const { stepsCount, currentStep } = getSteps({
    isMultipleProfessions: true,
    isMultipleEmploymentTypes,
    employmentType,
    formRequirements: parkInfo?.form_requirements,
    currentStep: "profession",
    isLoginWithPro: proId,
  });

  const { action: fetchSuggest, isFetching: isFetchingSuggest } = useAction({
    url: "/fleet-forms/selfreg/v1/suggest",
    method: "GET",
  });

  const { action: createProfile, isFetching: isSubmittingCreateProfile } =
    useAction({
      url: "/fleet-forms/selfreg/v1/submit",
      method: "POST",
    });

  const professionSettingsList = parkInfo?.profession_settings_list ?? [];

  if (!parkInfo) {
    return <NotFound />;
  }

  if (isFetchingSuggest || isSubmittingCreateProfile) {
    return <PageSpinner />;
  }

  const enableNewOffer = flag({
    id: "a56ea566-bb27-4972-8314-80e52e67e9c0",
    description: "Enable new offer",
  });
  const enableSerbiaOfferImage =
    parkInfo.park_id === "1a1d6c5f1ad7423481a1bc8a8c5d09db";

  return (
    <AppLayout
      title={
        isIframe
          ? undefined
          : message({
              id: "019a9c06-e9c3-72ca-a9cb-6e9da2968535",
              context: "Profession form. Title",
              default: "Profession",
            })
      }
      partnerName={parkInfo.partner_name ?? ""}
      content={
        <div
          css={{
            display: "grid",
            gridTemplateRows: "auto 1fr auto",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <FormHeader
            title={message({
              id: "019a9c34-6c8f-73f2-bb76-9a3abc4bdfd9",
              context: "Profession form. Title",
              default: "Select profession",
            })}
            step={
              currentStep
                ? { current: currentStep, total: stepsCount }
                : undefined
            }
          />
          <div
            css={{
              alignSelf: "start",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "start",
              ...cssFns.gap("16px"),
              ...cssFns.padding("16px"),
            }}
          >
            {professionSettingsList.map((profession) => {
              const professionId =
                profession.profession_id as keyof typeof professionsConfig;
              const employmentTypes =
                profession.employment_type_list?.map(
                  (employmentType) => employmentType.employment_type,
                ) ?? [];

              const isOnlyParkEmployee =
                employmentTypes.length === 1 &&
                employmentTypes[0] === "park_employee" &&
                (proId || leadId);

              const onlyOneEmploymentType =
                employmentTypes.length === 1 ? employmentTypes[0] : undefined;

              const { nextStep } = getSteps({
                isMultipleProfessions: true,
                isMultipleEmploymentTypes:
                  profession.employment_type_list &&
                  profession.employment_type_list.length > 1,
                employmentType: employmentType ?? onlyOneEmploymentType,
                formRequirements: profession.form_requirements,
                currentStep: "profession",
                isLoginWithPro: proId,
              });

              const searchParams = new URLSearchParams({
                ref_id: refId,
                ...(sessionId ? { sessionId } : undefined),
                ...(specification ? { specification } : undefined),
                ...(leadId ? { lead_id: leadId } : undefined),
                profession_id: professionId,
                ...(nextStep !== "employment-type" && employmentTypes[0]
                  ? { employment_type_id: employmentTypes[0] }
                  : undefined),
                ...(proId ? { pro_id: proId.toString() } : undefined),
                is_multiple_professions: "true",
                ...(isMultipleEmploymentTypes
                  ? { is_multiple_employment_types: "true" }
                  : undefined),
                ...(!isOnlyParkEmployee
                  ? { previous_step: "profession" }
                  : undefined),
                ...(confirmationId
                  ? { confirmation_id: confirmationId }
                  : undefined),
              });

              return (
                <LinkPanel
                  key={professionId}
                  onPress={() => {
                    logActionMultiProfessionForm("profession_click", {
                      params: { profession: professionId },
                    });

                    if (isOnlyParkEmployee) {
                      setFormErrorMessage(undefined);

                      fetchSuggest(
                        {
                          headers: {
                            language: locale.toString(),
                          },
                          query: {
                            ref_id: refId,
                            employment_type: "park_employee",
                            ...(leadId ? { lead_id: leadId } : undefined),
                            apply_auth: proId,
                          },
                        },
                        {
                          onResponse: (suggestRes) => {
                            if (suggestRes.status !== 200) {
                              setFormErrorMessage(errorMessages.default);
                              return;
                            }

                            const suggest = suggestRes.body;
                            const hasAllSuggestFields =
                              suggest.phone &&
                              suggest.name?.first &&
                              suggest.name.last;

                            if (!hasAllSuggestFields || !sessionId) {
                              setFormErrorMessage(errorMessages.default);
                              return;
                            }

                            createProfile(
                              {
                                headers: {
                                  "x-submission-id": sessionId,
                                  language: locale.toString(),
                                  "content-type": "application/json",
                                },
                                query: {
                                  ref_id: refId,
                                  apply_auth: proId,
                                },
                                body: {
                                  personal: {
                                    first_name: suggest.name!.first!,
                                    last_name: suggest.name!.last!,
                                    phone: suggest.phone!,
                                    agree_processing_data: true,
                                    agree_receive_info: true,
                                    profession_id: professionId,
                                    employment_type: "park_employee",
                                    confirmation_id: confirmationId
                                      ? confirmationId
                                      : undefined,
                                  },
                                },
                              },
                              {
                                onResponse: (submitRes) => {
                                  if (submitRes.status === 204) {
                                    navigate(`/${nextStep}?${searchParams}`);
                                  } else {
                                    setFormErrorMessage(
                                      submitErrorMessages[
                                        submitRes.body.code
                                      ] ?? errorMessages.default,
                                    );
                                  }
                                },
                              },
                            );
                          },
                        },
                      );
                    }
                  }}
                  icon={professionsConfig[professionId].icon}
                  label={professionsConfig[professionId].label}
                  subLabel={formatList(
                    employmentTypes.map(
                      (employmentType) => employmentTypeNames[employmentType],
                    ),
                  )}
                  description={professionsConfig[professionId].description}
                  link={
                    isOnlyParkEmployee
                      ? undefined
                      : profession.redirect_link
                        ? {
                            href: `${profession.redirect_link}?${new URLSearchParams(
                              {
                                ...(leadId ? { lead_id: leadId } : undefined),
                              },
                            )}`,
                          }
                        : makeLink(`/${nextStep}?${searchParams}`)
                  }
                />
              );
            })}
          </div>
          {formErrorMessage && (
            <span
              role="alert"
              aria-live="assertive"
              css={{
                ...typo({
                  level: "caption1",
                  weight: "regular",
                  density: "tight",
                }),
                textAlign: "center",
                ...cssFns.padding("12px", "16px"),
                color: uiColors.statuses.danger,
              }}
            >
              {formErrorMessage}
            </span>
          )}
        </div>
      }
      offer={
        isIframe ? undefined : enableNewOffer ? (
          <Offer
            offerText={parkInfo.promo_text}
            image={
              enableSerbiaOfferImage
                ? {
                    avif: SerbiaOfferAvif,
                    png: SerbiaOfferPng,
                    webp: SerbiaOfferWebp,
                    height: "130px",
                    width: "178px",
                    padding: "24px",
                  }
                : undefined
            }
            type={"multiple"}
            onPhonePress={() =>
              logActionContact("phone_click_v2", {
                params: { stage: "register" },
              })
            }
            onMessengerPress={() =>
              logActionContact("messenger_click_v2", {
                params: { stage: "register" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        ) : (
          <OfferDeprecated
            parkId={parkInfo.park_id}
            offerText={parkInfo.promo_text}
            isDelivery={specification === "delivery"}
            onPhonePress={() =>
              logActionContact("phone_click_v2", {
                params: { stage: "details" },
              })
            }
            onMessengerPress={() =>
              logActionContact("messenger_click_v2", {
                params: { stage: "details" },
              })
            }
            phone={parkInfo.phone}
            messageLink={parkInfo.message_link}
          />
        )
      }
    />
  );
};

export const ProfessionForm = ({ isIframe }: { isIframe: boolean }) => {
  const [
    {
      ref_id: refId,
      sessionId,
      specification,
      pro_id: proId,
      lead_id: leadId,
      is_multiple_employment_types: isMultipleEmploymentTypes,
      employment_type_id: employmentType,
      confirmation_id: confirmationId,
    },
  ] = useSearchParams({
    ref_id: t.string,
    sessionId: t.string,
    specification: t.string,
    pro_id: t.boolean,
    lead_id: t.string,
    is_multiple_employment_types: t.boolean,
    employment_type_id: t.enumString([
      "selfemployed",
      "individual_entrepreneur",
      "park_employee",
    ] as const),
    confirmation_id: t.string,
  });

  if (!refId) {
    return <NotFound />;
  }

  return (
    <Content
      refId={refId}
      sessionId={sessionId}
      specification={specification}
      proId={proId}
      leadId={leadId}
      isMultipleEmploymentTypes={isMultipleEmploymentTypes}
      employmentType={employmentType}
      isIframe={isIframe}
      confirmationId={confirmationId}
    />
  );
};

const useProfessionsConfig = () => {
  const message = useMessage();

  return {
    taxi_driver_rental: {
      icon: "rental-car-taxi-profession",
      label: message({
        id: "019a9c56-8646-76f3-95d0-2d256003ed56",
        context: "Profession form. Rental car taxi profession label",
        default: "Driver with rented vehicle",
      }),
      description: message({
        id: "019b26c3-cb58-71bb-860b-f0d53d75b51d",
        context: "Profession form. Rental car taxi profession description",
        default: `Age from 21
Category B, minimum 3 years of driving experience
Car rental available
Can be combined with delivery`,
      }),
    },
    taxi_driver_own_car: {
      icon: "personal-car-taxi-profession",
      label: message({
        id: "019a9c56-90aa-73cc-b86c-1cbbcd955b11",
        context: "Profession form. Personal car taxi profession label",
        default: "Driver with own vehicle",
      }),
      description: message({
        id: "019b26c3-eb43-77ac-a92b-385a61469314",
        context: "Profession form. Personal car taxi profession description",
        default: `Age from 21
Category B, minimum 3 years of driving experience
Can be combined with delivery`,
      }),
    },
    intercity: {
      icon: "intercity-driver-profession",
      label: message({
        id: "019a9c56-a485-741c-aa26-a5d6e3a61207",
        context: "Profession form. Intercity driver profession label",
        default: "City to City driver",
      }),
      description: message({
        id: "019b26c4-0da5-7756-b7f0-5fb6a0fe3e2c",
        context: "Profession form. Intercity driver profession description",
        default: `Age 21 and up
Category B, minimum 3 years of driving experience
Car rental available`,
      }),
    },
    cargo_courier_on_foot: {
      icon: "walking-courier-profession",
      label: message({
        id: "019a9c07-2809-752a-8925-34b2d0d81df4",
        context: "Profession form. Walking courier profession label",
        default: "On-foot courier",
      }),
      description: message({
        id: "019b26c4-25d7-717d-8eec-22a1c44973a7",
        context: "Profession form. Walking courier profession description",
        default: `Parcels from people, orders from stores
Must be 18 years old or older
No driver's license required`,
      }),
    },
    cargo_courier_on_bicycle: {
      icon: "bicycle-courier-profession",
      label: message({
        id: "019a9c07-32e3-713f-830b-2c5108784c7d",
        context: "Profession form. Bicycle courier profession label",
        default: "Bicycle courier",
      }),
      description: message({
        id: "019b26c4-3b3c-773f-8af5-02fe9564aaee",
        context: "Profession form. Bicycle courier profession description",
        default: `Parcels from people, orders from stores
Must be 18 years old or older
No driver's license required`,
      }),
    },
    cargo_courier_on_scooter: {
      icon: "scooter-courier-profession",
      label: message({
        id: "019a9c07-3fe0-71a0-a4cc-ce8b279f5a6d",
        context: "Profession form. Scooter courier profession label",
        default: "Scooter courier",
      }),
      description: message({
        id: "019b26c4-4d0d-727e-9bc9-c593ed0e8811",
        context: "Profession form. Scooter courier profession description",
        default: `Parcels from people, orders from stores
Age from 18 years
Without a driver's license`,
      }),
    },
    cargo_courier_on_moto: {
      icon: "motorcycle-courier-profession",
      label: message({
        id: "019a9c07-4ae0-7649-b72c-5a23b77b8869",
        context: "Profession form. Motorcycle courier profession label",
        default: "Motorcycle courier",
      }),
      description: message({
        id: "019b26c4-6109-769a-b7ce-52c147eeb3bc",
        context: "Profession form. Motorcycle courier profession description",
        default: `No carrier's license required
Parcels from people and orders from stores
Category A, any experience
You can drive your own motorcycle up to 20 years old`,
      }),
    },
    cargo_courier_on_car: {
      icon: "car-courier-profession",
      label: message({
        id: "019a9c07-5386-7302-9881-cc1983bee09f",
        context: "Profession form. Car courier profession label",
        default: "Driver courier",
      }),
      description: message({
        id: "019b26c4-7636-71d3-b91f-72cf2900c072",
        context: "Profession form. Car courier profession description",
        default: `No carrier's license required
Parcels from people and orders from stores
Category B, any experience
You can drive your own car up to 20 years old`,
      }),
    },
    cargo_courier_on_truck: {
      icon: "truck-courier-profession",
      label: message({
        id: "019a9c07-6087-714a-92b3-fab28503141c",
        context: "Profession form. Truck courier profession label",
        default: "Cargo courier",
      }),
      description: message({
        id: "019b26ee-d4d6-774e-8e54-9583d6c6fe65",
        context: "Profession form. Truck courier profession description",
        default: `No carrier's license required
Parcels from people and orders from stores
Category C, any experience
You can drive your own car up to 20 years old`,
      }),
    },
    "shift-worker": {
      icon: "shift-worker-profession",
      label: message({
        id: "019b26ee-e0f9-76a8-8a1b-f16ab8809472",
        context: "Profession form. Shift worker profession label",
        default: "Shift contractor",
      }),
      description: message({
        id: "019b26c4-9eac-734d-b014-81104cd899fa",
        context: "Profession form. Shift worker profession description",
        default: `Part-time work with no experience, convenient for you and close to home
Medical insurance coverage after 1 shift, up to ₽3,000
Payments within 72 hours`,
      }),
    },
  } as const;
};

const useEmploymentTypeNames = () => {
  const message = useMessage();

  return {
    park_employee: message({
      id: "019a9c07-7553-742c-8f81-f64ef6adf06a",
      context: "Profession form. Park employee employment type name",
      default: "Fleet contractor",
    }),
    selfemployed: message({
      id: "019a9c07-7ee2-72ca-961a-23626b43e26e",
      context: "Profession form. Selfemployed employment type name",
      default: "Fleet self-employed",
    }),
    individual_entrepreneur: message({
      id: "019a9c07-8851-7799-8724-2350f2424a21",
      context: "Profession form. Individual entrepreneur employment type name",
      default: "Individual entrepreneur",
    }),
  } as const;
};
